import React, { FC, useCallback } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { CoreModal, CoreModalProps } from '../../../../_core/components/_ui/core-modal';
import { CoreButton } from '../../../../_core/components/_ui/core-button';
import { CreateDistrictDto, SurveyTemplateDto, SurveyTemplateTranslateDto } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import {
	containerButtonStyles,
	leftButtonStyles,
	inputBoxStyles,
	modalBoxStyles,
	errorTextStyles,
	selectListTypographyOverflowStyles,
} from './create-form.styles';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { AutocompleteTextField, CoreAutoComplete } from '../../../../_core/components/_ui/core-autocomplete';

export type CreateSurveyFormModalProps = CoreModalProps & {
	surveyTemplatesList?: SurveyTemplateDto[];
	formError?: any;
	submitAction(id: string): Promise<void>;
	closeModal(): void;
};

export const CreateSurveyFormModal: FC<CreateSurveyFormModalProps> = (props) => {
	const { surveyTemplatesList, formError, submitAction, closeModal, ...modalProps } = props;
	const [t] = useTranslation();

	const defaultValues: any = { surveyTemplate: '' };

	const {
		register,
		control,
		handleSubmit,
		getValues,
		formState: { errors, isDirty, isLoading, isSubmitting, isSubmitted, isValid },
	} = useForm({
		defaultValues,
		mode: 'all',
	});

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const handleFormSubmit = useCallback(
		async (values: any) => {
			await submitAction(values.surveyTemplate._id);
		},
		[submitAction]
	);

	return (
		<CoreModal {...modalProps} bodySx={{ pt: 0 }} modalWidth={500} paperSx={{ width: '100%' }}>
			<Typography variant="h5" sx={mainTitleModalWindowStyles}>
				{t('surveys.forms.choose-survey')}
			</Typography>

			<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
				<CoreAutoComplete
					label={t('surveys.forms.labels.name')}
					fullWidth
					requiredMark
					ruleIsRequired
					ruleName="Template"
					{...register('surveyTemplate')}
					control={control}
					error={!!errors['surveyTemplate']}
					helperText={errors['surveyTemplate'] ? (errors['surveyTemplate'] as FieldError).message : undefined}
					options={surveyTemplatesList ?? []}
					getOptionLabel={(option) =>
						option.title ? option.title : ''
					}
					selectOnFocus={true}
					isOptionEqualToValue={(option, value) => option._id === value._id}
					disableClearable={!getValues('surveyTemplate')}
					renderInput={(params) => (
						<AutocompleteTextField
							{...params}
							error={!!errors['surveyTemplate']}
							placeholder={t('surveys.forms.placeholders.template')}
							sx={{
								'& .MuiOutlinedInput-root': {
									'&.Mui-focused fieldset': {
										borderColor: (theme) => theme.colors.blue,
									},
								},
							}}
						/>
					)}
					renderOption={(props, option) => (
						<MenuItem
							{...props}
							key={option._id}
							sx={{ display: 'flex', alignItems: 'center' }}
						>
							<Typography sx={selectListTypographyOverflowStyles}>{`${option.title}`}</Typography>
						</MenuItem>
					)}
				/>
				{formError && (
					<Typography sx={errorTextStyles}>
						{formError?.data?.message ? formError.data.message : t('errors.invalid-credentials-error')}
					</Typography>
				)}
				<Box sx={containerButtonStyles}>
					<CoreButton onClick={closeModal} variant="secondary" sx={mergeSx(buttonModalWindowStyles, leftButtonStyles)}>
						{t('districts.forms.buttons.close-form')}
					</CoreButton>
					<CoreButton
						type="submit"
						contraModeStyleButton
						disabled={disableSubmit}
						loading={loadingSubmit}
						sx={buttonModalWindowStyles}
					>
						{t('surveys.choose-button')}
					</CoreButton>
				</Box>
			</Box>
		</CoreModal>
	);
};
